// @flow
import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import classNames from 'classnames';

import { VIEW_SIZE } from 'utils/device';
import Slider from 'shared-components/Slider/Slider';
import SliderControls from 'shared-components/SliderControls/SliderControls';
import DesktopControls from './DesktopControls/DesktopControls';
import GalleryImage from './GalleryImage';
import './Gallery.css';

const MAX_THUMBNAILS = 7;
const PDP_BOX_WIDTH = {
  desktop: 528,
  tablet: 389,
};

type Props = {|
  +images: Array<string>,
  +viewSize: string,
  +context: string,
  +isLowInventory?: boolean,
  +disableBodyScroll: (HTMLElement) => void,
  +disableBodyScrollAccordion: (void) => void,
  +clearAllBodyScrollLocks: (void) => void,
|};

type State = {|
  +currImage: number,
  +zoomedIn: boolean,
  +zoomClicked: boolean,
  +animateLeft: boolean,
  +imageX: number,
  +imageY: number,
  +swipeStartX: number,
  +swipeDelta: number,
  +swiping: boolean,
  +boxWidth: number,
  +firstThumbnail: number,
|};

class Gallery extends React.PureComponent<Props, State> {
  state = {
    currImage: 0,
    zoomedIn: false,
    zoomClicked: false,
    animateLeft: false,
    imageX: 0,
    imageY: 0,
    swipeStartX: 0,
    swipeDelta: 0,
    swiping: false,
    boxWidth: 0,
    firstThumbnail: 0,
  };

  componentDidMount() {
    this.setBoxWidth();
    window.addEventListener('resize', this.setBoxWidth);
  }

  componentDidUpdate(prevProps: Props) {
    const previousImages = prevProps.images;
    const updatedImages = this.props.images;
    if (!isEqual(previousImages, updatedImages)) this.updateCurrImage(0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setBoxWidth);
  }

  sliderFrame = React.createRef<HTMLDivElement>();

  setBoxWidth = () => {
    const { context, viewSize } = this.props;
    const isPdP = context === 'pdp';
    if (isPdP && viewSize === VIEW_SIZE.xLarge) {
      this.setState({ boxWidth: PDP_BOX_WIDTH.desktop });
    } else if (
      isPdP &&
      (viewSize === VIEW_SIZE.large || viewSize === VIEW_SIZE.medium)
    ) {
      this.setState({ boxWidth: PDP_BOX_WIDTH.tablet });
    } else {
      const currentSliderFrame = this.sliderFrame.current;
      if (currentSliderFrame !== null) {
        this.setState({ boxWidth: currentSliderFrame.clientWidth });
      }
    }
  };

  handleClickThumbnail = (imageIndex: number) => {
    this.updateCurrImage(imageIndex);

    if (this.props.images.length > MAX_THUMBNAILS) {
      this.slideThumbnails(imageIndex);
    }
  };

  slideThumbnails = (clickedImageIndex: number) => {
    const { images } = this.props;
    const maxIndex = images.length - MAX_THUMBNAILS;
    this.setState(({ firstThumbnail }) => {
      let newFirstThumbnail = firstThumbnail;

      if (clickedImageIndex <= firstThumbnail) {
        newFirstThumbnail = Math.max(firstThumbnail - MAX_THUMBNAILS + 1, 0);
      }

      if (clickedImageIndex >= firstThumbnail + MAX_THUMBNAILS - 1) {
        newFirstThumbnail = Math.min(clickedImageIndex, maxIndex);
      }

      return { firstThumbnail: newFirstThumbnail };
    });
  };

  updateCurrImage = (i: number) => {
    this.setState({ currImage: i });
  };

  toggleCurrImage = (delta: number) => {
    this.setState((state) => ({ currImage: state.currImage + delta }));
  };

  handleClickZoom = () => this.setState({ zoomClicked: true });

  zoomIn = () => {
    this.state.zoomClicked && this.setState({ zoomedIn: true });
  };

  zoomOut = () => this.setState({ zoomedIn: false, zoomClicked: false });

  handleMouseMove = ({ nativeEvent }: SyntheticMouseEvent<>) => {
    const { setTransformOriginVal } = this;
    const { target } = nativeEvent;
    if (target instanceof HTMLElement) {
      this.setState({
        imageX: setTransformOriginVal(nativeEvent.offsetX, target.clientWidth),
        imageY: setTransformOriginVal(nativeEvent.offsetY, target.clientHeight),
      });
    }
  };

  setTransformOriginVal = (offset: number, boxLength: number) =>
    50 - 50 * ((boxLength / 2 - offset) / (boxLength / 2));

  handleSwipeStart = (e: SyntheticTouchEvent<>) => {
    const touchEvent = e.targetTouches[0];
    const swipeStartX = touchEvent.pageX;
    this.setState({ swipeStartX, swiping: true });
  };

  handleSwipeMove = throttle(
    (e: SyntheticTouchEvent<>) => {
      const x = e.targetTouches[0].pageX;
      const updateState = ({ swipeStartX, currImage, boxWidth }, props) => {
        let swipeDelta = x - swipeStartX;
        const swipeIsInvalid =
          (currImage === 0 && swipeDelta > 0) ||
          (currImage === props.images.length - 1 && swipeDelta < 0);
        swipeDelta = swipeIsInvalid ? 0 : swipeDelta;
        return { swipeDelta };
      };
      this.setState(updateState);
    },
    100,
    { trailing: false }
  );

  handleSwipeEnd = (e: SyntheticTouchEvent<>) => {
    let { swipeDelta, currImage } = this.state;
    const swipingLeft = swipeDelta < 0;
    const shouldUpdateCurrImage = Math.abs(swipeDelta) > 5;
    if (shouldUpdateCurrImage) {
      currImage = swipingLeft ? currImage + 1 : currImage - 1;
    }
    this.setState({
      swipeDelta: 0,
      swipeStartX: 0,
      swiping: false,
      currImage,
    });
  };

  render() {
    const {
      zoomOut,
      zoomIn,
      handleMouseMove,
      updateCurrImage,
      handleClickThumbnail,
      toggleCurrImage,
      handleClickZoom,
      handleSwipeStart,
      handleSwipeMove,
      handleSwipeEnd,
    } = this;
    const { images, viewSize, context, isLowInventory } = this.props;
    const {
      currImage,
      zoomedIn,
      imageX,
      imageY,
      swipeDelta,
      swiping,
      firstThumbnail,
      boxWidth,
    } = this.state;
    const isMobile = viewSize === VIEW_SIZE.small;
    const { innerWidth } = window;
    const left =
      zoomedIn && isMobile
        ? currImage * -innerWidth
        : -boxWidth * currImage + swipeDelta;

    return (
      <div
        onMouseLeave={zoomOut}
        onMouseMove={zoomedIn && !isMobile ? handleMouseMove : null}
        onTouchStart={zoomedIn ? null : handleSwipeStart}
        onTouchMove={zoomedIn ? null : handleSwipeMove}
        onTouchEnd={zoomedIn ? null : handleSwipeEnd}
        className={classNames('zoom-image-box-container', {
          mobile: isMobile,
          zoomed: zoomedIn,
          [`zoom-image-box-container-${context}`]: context,
          'png-image': images[currImage].includes('.png'),
        })}
        ref={this.sliderFrame}
      >
        <Slider
          left={left}
          swiping={swiping}
          context={context}
          count={size(images)}
          currSlide={currImage}
        >
          {images.map((img, i) => (
            <GalleryImage
              key={img}
              img={img}
              currImage={currImage}
              index={i}
              imageX={imageX}
              imageY={imageY}
              imagesLength={images.length}
            />
          ))}
        </Slider>
        {isMobile ? (
          <SliderControls
            currSlide={currImage}
            updateCurrSlide={updateCurrImage}
            onClickLeft={() => toggleCurrImage(-1)}
            onClickRight={() => toggleCurrImage(1)}
            count={size(images)}
          />
        ) : (
          <DesktopControls
            images={images}
            currImage={currImage}
            handleClickThumbnail={handleClickThumbnail}
            firstThumbnail={firstThumbnail}
            maxThumbnails={MAX_THUMBNAILS}
            context={context}
          />
        )}
        {isLowInventory && (
          <div
            className={classNames('low-inventory', {
              'slider-mobile': isMobile && size(images) > 1,
            })}
          >
            Hurry, Low Inventory!
          </div>
        )}
      </div>
    );
  }
}

Gallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  viewSize: PropTypes.string.isRequired,
  context: PropTypes.string,
  disableBodyScroll: PropTypes.func,
  disableBodyScrollAccordion: PropTypes.func,
  clearAllBodyScrollLocks: PropTypes.func,
};

Gallery.defaultProps = {
  context: 'accordion',
  isLowInventory: false,
  disableBodyScroll: () => {},
  disableBodyScrollAccordion: () => {},
  clearAllBodyScrollLocks: () => {},
};

export default Gallery;
